import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Download, Printer, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';

interface Row {
  id: number;
  nome: string;
  seguradora: string;
  apolice: string;
  vencSeguro: string;
  vencDoc: string;
  pior: number; // menor nº de dias até vencer (p/ ordenar); 99999 se sem datas
}

function dias(v: string): number | null {
  if (!v) return null;
  const t = new Date(`${v}T00:00:00`).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 86_400_000);
}
function statusOf(d: number | null): { label: string; tone: string } {
  if (d == null) return { label: '—', tone: 'text-muted-foreground' };
  if (d < 0) return { label: `Vencido há ${-d}d`, tone: 'text-status-blocked' };
  if (d <= 30) return { label: `Vence em ${d}d`, tone: 'text-status-ready' };
  return { label: `Em dia (${d}d)`, tone: 'text-status-running' };
}
const str = (a: Record<string, unknown>, k: string) => (typeof a[k] === 'string' ? (a[k] as string) : '');

export function InsuranceReportPage() {
  const devices = useFleetStore((s) => s.devices);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const d of devices.values()) {
      const a = d.attributes ?? {};
      const vs = str(a, 'vencSeguro');
      const vd = str(a, 'vencDoc');
      const ds = dias(vs);
      const dd = dias(vd);
      const candidatos = [ds, dd].filter((x): x is number => x != null);
      out.push({
        id: d.id, nome: d.name, seguradora: str(a, 'seguradora'), apolice: str(a, 'apolice'),
        vencSeguro: vs, vencDoc: vd, pior: candidatos.length ? Math.min(...candidatos) : 99999,
      });
    }
    return out.sort((a, b) => a.pior - b.pior);
  }, [devices]);

  const venc = rows.filter((r) => r.pior < 0).length;
  const prox = rows.filter((r) => r.pior >= 0 && r.pior <= 30).length;

  const exportCsv = () => {
    const head = ['embarcacao', 'seguradora', 'apolice', 'venc_seguro', 'venc_documento'];
    const data = rows.map((r) => [r.nome, r.seguradora, r.apolice, r.vencSeguro, r.vencDoc]);
    const csv = [head, ...data].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'seguros-documentos.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <Link to="/reports" className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5 print:hidden">
        <ChevronLeft className="h-5 w-5" /> Relatórios
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Seguros & Documentos</h1>
          <p className="text-sm text-muted-foreground">Vencimentos por embarcação (seguro e documento).</p>
        </div>
        <div className="flex gap-1.5 print:hidden">
          <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-semibold hover:bg-accent"><Download className="h-3.5 w-3.5" /> CSV</button>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-semibold hover:bg-accent"><Printer className="h-3.5 w-3.5" /> PDF</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px] font-bold">
        <span className="rounded-full bg-status-blocked/10 px-2 py-0.5 text-status-blocked">{venc} vencidos</span>
        <span className="rounded-full bg-status-ready/10 px-2 py-0.5 text-status-ready">{prox} a vencer (30d)</span>
        <span className="rounded-full bg-secondary px-2 py-0.5">{rows.length} embarcações</span>
      </div>

      {rows.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center gap-3 p-10 text-center"><ShieldCheck className="h-10 w-10 text-muted-foreground/50" /><p className="text-sm text-muted-foreground">Nenhuma embarcação na frota.</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
            <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-border text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">Embarcação</th>
                  <th className="px-3 py-2 font-semibold">Seguradora</th>
                  <th className="px-3 py-2 font-semibold">Apólice</th>
                  <th className="px-3 py-2 font-semibold">Seguro</th>
                  <th className="px-3 py-2 font-semibold">Documento</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ss = statusOf(dias(r.vencSeguro));
                  const sd = statusOf(dias(r.vencDoc));
                  return (
                    <tr key={r.id} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-2 font-medium"><Link to={`/devices/${r.id}`} className="hover:underline">{r.nome}</Link></td>
                      <td className="px-3 py-2">{r.seguradora || '—'}</td>
                      <td className="px-3 py-2">{r.apolice || '—'}</td>
                      <td className={cn('px-3 py-2 font-semibold', ss.tone)}>{ss.label}</td>
                      <td className={cn('px-3 py-2 font-semibold', sd.tone)}>{sd.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
